/**
 * TourLauncher — Botón "Tour" en topbar + Modal de configuración del tour.
 * Sprint 2: Product Tour
 *
 * Props:
 *   onStartTour({ mode, length }) — callback que activa OnboardingTour con los params
 */

import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import {
  PlayCircle,
  CaretDown as ChevronDown,
  Check,
  X,
} from '@phosphor-icons/react';

// ── Radix Select primitivo con estilos Onyx ──────────────────────────────────

const SelectItem = React.forwardRef(({ children, ...props }, ref) => (
  <Select.Item
    ref={ref}
    {...props}
    className="relative flex items-center gap-2 px-3 py-2 text-[13px] text-slate-300 rounded-md cursor-pointer outline-none
      data-[highlighted]:bg-cyan-500/[0.08] data-[highlighted]:text-v4-on-accent
      data-[state=checked]:text-cyan-400"
  >
    <Select.ItemText>{children}</Select.ItemText>
    <Select.ItemIndicator className="absolute right-2">
      <Check size={12} className="text-cyan-400" />
    </Select.ItemIndicator>
  </Select.Item>
));
SelectItem.displayName = 'SelectItem';

const OnyxSelect = ({ value, onValueChange, placeholder, children }) => (
  <Select.Root value={value} onValueChange={onValueChange}>
    <Select.Trigger
      className="w-full flex items-center justify-between gap-2 px-3 py-2
        bg-slate-950/60 border border-white/[0.08] rounded-lg text-[13px] text-slate-200
        hover:border-cyan-500/[0.25] focus:outline-none focus:border-cyan-500/40
        transition-colors"
      aria-label={placeholder}
    >
      <Select.Value placeholder={placeholder} />
      <Select.Icon>
        <ChevronDown size={14} className="text-slate-500" />
      </Select.Icon>
    </Select.Trigger>

    <Select.Portal>
      <Select.Content
        className="z-[10000] min-w-[220px] bg-slate-900 border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden p-1"
        position="popper"
        sideOffset={4}
      >
        <Select.Viewport>{children}</Select.Viewport>
      </Select.Content>
    </Select.Portal>
  </Select.Root>
);

// ── Componente principal ──────────────────────────────────────────────────────

const TourLauncher = ({ onStartTour, collapsed = false }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('onboarding');
  const [length, setLength] = useState('quick');

  const handleStart = () => {
    setOpen(false);
    onStartTour({ mode, length });
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {/* Trigger — botón persistente en topbar */}
      <Dialog.Trigger asChild>
        <button
          type="button"
          data-testid="tour-launcher-btn"
          title="Iniciar tour guiado"
          aria-label="Iniciar tour guiado"
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium
            text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/[0.06]
            border border-transparent hover:border-cyan-500/[0.15]
            transition-all duration-200
            ${collapsed ? 'justify-center px-2' : ''}
          `}
        >
          <PlayCircle size={15} weight="regular" aria-hidden="true" />
          {!collapsed && <span>Tour</span>}
        </button>
      </Dialog.Trigger>

      {/* Modal */}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[9990] bg-black/50 backdrop-blur-sm" />

        <Dialog.Content
          className="fixed z-[9991] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[440px] max-w-[calc(100vw-2rem)]
            bg-slate-900/95 backdrop-blur-xl border border-white/[0.08]
            rounded-xl shadow-2xl overflow-hidden"
          aria-labelledby="tour-modal-title"
          aria-describedby="tour-modal-desc"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-cyan-500/[0.08] border border-cyan-500/[0.12] flex items-center justify-center shrink-0">
                <PlayCircle size={18} className="text-cyan-400" weight="regular" aria-hidden="true" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400/90">
                  // PRODUCT TOUR
                </div>
                <Dialog.Title
                  id="tour-modal-title"
                  className="mt-0.5 text-[15px] font-semibold text-slate-100"
                >
                  Iniciar tour guiado
                </Dialog.Title>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Cerrar"
                className="p-1.5 text-slate-500 hover:text-slate-200 rounded-md hover:bg-v4-panel/[0.06] transition-colors"
              >
                <X size={16} weight="regular" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <Dialog.Description id="tour-modal-desc" className="text-[13px] text-slate-400 leading-relaxed">
              Elige el tipo de tour y la duración. Podrás pausarlo en cualquier momento.
            </Dialog.Description>

            {/* Modo */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">
                Modo
              </label>
              <OnyxSelect value={mode} onValueChange={setMode} placeholder="Seleccionar modo">
                <SelectItem value="prospect">Demo comercial</SelectItem>
                <SelectItem value="onboarding">Onboarding técnico</SelectItem>
              </OnyxSelect>
            </div>

            {/* Granularidad */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">
                Duración
              </label>
              <OnyxSelect value={length} onValueChange={setLength} placeholder="Seleccionar duración">
                <SelectItem value="quick">Tour rápido (2 min)</SelectItem>
                <SelectItem value="full">Tour completo (15 min)</SelectItem>
              </OnyxSelect>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/[0.06] bg-slate-950/40">
            <Dialog.Close asChild>
              <button
                type="button"
                className="px-4 py-2 text-[12px] font-medium text-slate-400 hover:text-slate-200 transition-colors"
              >
                Cancelar
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleStart}
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold
                text-slate-950 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors"
            >
              <PlayCircle size={14} weight="bold" aria-hidden="true" />
              Empezar
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { TourLauncher };
export default TourLauncher;
